import React, { useState, useEffect } from 'react';
import {
    Button, Typography, TextField, Box, IconButton,
    InputAdornment,
    MenuItem,
    FormControlLabel,
    Checkbox,
    FormGroup,
    FormHelperText,
    Paper,
    Divider,
    Avatar,
    RadioGroup,
    Radio
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { Visibility, VisibilityOff, PersonAdd, Email, Phone, Business, Lock } from '@mui/icons-material';
import toast from 'react-hot-toast';
import LoginDialog from '../../Components/CustomizedModels/LoginModel';
import CloseIcon from '@mui/icons-material/Close';
import { postCreateAdminAction, getAllAdminsAction } from '../../store/actions';
import { useDispatch, useSelector } from 'react-redux';

const AdminCreation = ({ onClose }) => {
    const dispatch = useDispatch();
    const [showPassword, setShowPassword] = useState(false);
    const [mobile_no, setPhoneNumber] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [openLogin, setOpenLogin] = useState(false);

    const { successData } = useSelector(state => state.postCreateAdminReducer);

    // Define the common style for all TextFields
    const textFieldStyle = {
        '& .MuiOutlinedInput-root': {
            borderRadius: '12px',
            width: '100%',
            transition: 'all 0.3s ease-in-out',
            '&:hover': {
                boxShadow: '0 2px 8px rgba(0, 123, 255, 0.15)',
            },
            '&.Mui-focused': {
                boxShadow: '0 4px 12px rgba(0, 123, 255, 0.2)',
            }
        },
        '& .MuiInputLabel-root': {
            color: '#555',
            fontWeight: 500,
        },
        '& .MuiInputAdornment-root': {
            '& .MuiIconButton-root': {
                color: '#007bff',
            }
        }
    };

    const formik = useFormik({
        initialValues: {
            full_name: '',
            email: '',
            password: '',
            mobile_no: '',
            company: '',
            subscription_plan: '',
        },
        validationSchema: Yup.object({
            full_name: Yup.string().required('Full Name is required'),
            email: Yup.string().email('Invalid email').required('Email is required'),
            password: Yup.string().min(6, 'Password must be at least 6 characters').required('Password is required'),
            mobile_no: Yup.string().matches(/^[6-9]\d{9}$/, 'Invalid phone number').required('Phone Number is required'),
            subscription_plan: Yup.string().required('Subscription plan is required'),
        }),
        onSubmit: (values) => {
            setIsLoading(true);
            dispatch(postCreateAdminAction(values));
            console.log('Submitting form data:', values);
        }
    });

    const handleLoginClose = () => {
        setOpenLogin(false);
    };

    const handleOtpLogin = () => {
        setOpenLogin(false);
    };

    const handleLoginSuccess = () => {
        toast.success('Logged in successfully');
        handleLoginClose();
    };

    const handleLoginError = (errorMessage) => {
        toast.error(errorMessage || 'Login failed');
        console.error('Login failed:', errorMessage);
    };

    useEffect(() => {
        if (successData) {
            console.log('Admin created successfully:', successData);
            toast.success(successData.message);
            setIsLoading(false);
            onClose();
        }
        return () => {
            dispatch(getAllAdminsAction());
        };
    }, [successData, onClose]);

    return (
        <Box sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            backgroundColor: 'rgba(255, 255, 255, 0.95)',
            padding: { xs: '20px', sm: '30px', md: '50px' },
            borderRadius: '16px',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)'
        }}>
            <IconButton
                onClick={onClose}
                sx={{
                    position: 'absolute',
                    top: 10,
                    right: 10,
                    zIndex: 1,
                    backgroundColor: 'rgba(0, 0, 0, 0.05)',
                    '&:hover': {
                        backgroundColor: 'rgba(0, 0, 0, 0.1)',
                    }
                }}
            >
                <CloseIcon />
            </IconButton>

            <Paper elevation={0} sx={{
                width: '100%',
                backgroundColor: '#fff',
                display: 'flex',
                flexDirection: 'column',
                borderRadius: '16px',
                overflow: 'hidden',
                padding: { xs: '20px', sm: '30px' }
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <Avatar sx={{
                        backgroundColor: '#007bff',
                        mr: 2,
                        width: 48,
                        height: 48
                    }}>
                        <PersonAdd />
                    </Avatar>
                    <Typography variant="h5" sx={{
                        fontWeight: 'bold',
                        color: '#333',
                        fontSize: { xs: '1.5rem', sm: '1.75rem' }
                    }}>
                        Create New Admin
                    </Typography>
                </Box>

                <Divider sx={{ mb: 3 }} />

                <form onSubmit={formik.handleSubmit}>
                    <Grid container spacing={3}>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Full Name"
                                name="full_name"
                                variant="outlined"
                                size="small"
                                value={formik.values.full_name}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                error={formik.touched.full_name && Boolean(formik.errors.full_name)}
                                helperText={formik.touched.full_name && formik.errors.full_name}
                                sx={textFieldStyle}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <PersonAdd fontSize="small" sx={{ color: '#007bff' }} />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Company Name"
                                name="company"
                                variant="outlined"
                                size="small"
                                value={formik.values.company}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                error={formik.touched.company && Boolean(formik.errors.company)}
                                helperText={formik.touched.company && formik.errors.company}
                                sx={textFieldStyle}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Business fontSize="small" sx={{ color: '#007bff' }} />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Email Address"
                                name="email"
                                variant="outlined"
                                size="small"
                                type="email"
                                value={formik.values.email}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                error={formik.touched.email && Boolean(formik.errors.email)}
                                helperText={formik.touched.email && formik.errors.email}
                                sx={textFieldStyle}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Email fontSize="small" sx={{ color: '#007bff' }} />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Phone Number"
                                name="mobile_no"
                                variant="outlined"
                                size="small"
                                value={formik.values.mobile_no}
                                onChange={(e) => {
                                    formik.handleChange(e);
                                    setPhoneNumber(e.target.value);
                                }}
                                onBlur={formik.handleBlur}
                                error={formik.touched.mobile_no && Boolean(formik.errors.mobile_no)}
                                helperText={formik.touched.mobile_no && formik.errors.mobile_no}
                                inputProps={{ maxLength: 10 }}
                                sx={textFieldStyle}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Phone fontSize="small" sx={{ color: '#007bff' }} />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Password"
                                name="password"
                                variant="outlined"
                                size="small"
                                type={showPassword ? "text" : "password"}
                                value={formik.values.password}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                error={formik.touched.password && Boolean(formik.errors.password)}
                                helperText={formik.touched.password && formik.errors.password}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Lock fontSize="small" sx={{ color: '#007bff' }} />
                                        </InputAdornment>
                                    ),
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton onClick={() => setShowPassword(!showPassword)}>
                                                {showPassword ? <VisibilityOff /> : <Visibility />}
                                            </IconButton>
                                        </InputAdornment>
                                    )
                                }}
                                sx={textFieldStyle}
                            />
                        </Grid>

                        <Grid item xs={12} sx={{ mt: 1 }}>
                            <Paper elevation={0} sx={{
                                p: 2,
                                backgroundColor: 'rgba(0, 123, 255, 0.05)',
                                borderRadius: '12px',
                                border: '1px solid rgba(0, 123, 255, 0.2)'
                            }}>
                                <Typography variant="subtitle1" sx={{
                                    fontWeight: 'bold',
                                    mb: 1,
                                    color: '#007bff'
                                }}>
                                    Subscription Type
                                </Typography>
                                <RadioGroup
                                    name="subscription_plan"
                                    value={formik.values.subscription_plan}
                                    onChange={formik.handleChange}
                                    row
                                    sx={{ justifyContent: 'space-around' }}
                                >
                                    <FormControlLabel
                                        value="monthly"
                                        control={
                                            <Radio
                                                color="primary"
                                                sx={{
                                                    '&.Mui-checked': { color: '#007bff' },
                                                    transform: 'scale(1.1)'
                                                }}
                                            />
                                        }
                                        label={
                                            <Typography sx={{ fontWeight: 500 }}>
                                                Monthly Subscription
                                            </Typography>
                                        }
                                    />
                                    <FormControlLabel
                                        value="annual"
                                        control={
                                            <Radio
                                                color="primary"
                                                sx={{
                                                    '&.Mui-checked': { color: '#007bff' },
                                                    transform: 'scale(1.1)'
                                                }}
                                            />
                                        }
                                        label={
                                            <Typography sx={{ fontWeight: 500 }}>
                                                Yearly Subscription
                                            </Typography>
                                        }
                                    />
                                </RadioGroup>
                                {formik.touched.subscription_plan && formik.errors.subscription_plan && (
                                    <FormHelperText error sx={{ textAlign: 'center', mt: 1 }}>
                                        {formik.errors.subscription_plan}
                                    </FormHelperText>
                                )}
                            </Paper>
                        </Grid>

                        <Grid item xs={12} sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
                            <Button
                                type="submit"
                                fullWidth
                                variant="contained"
                                disabled={isLoading}
                                sx={{
                                    backgroundColor: '#007bff',
                                    color: '#fff',
                                    borderRadius: '12px',
                                    padding: '6px 24px',  // Reduced top/bottom, increased left/right
                                    maxWidth: '300px',    // Make button smaller by limiting width
                                    fontWeight: 'bold',
                                    textTransform: 'none',
                                    fontSize: '1rem',
                                    boxShadow: '0 4px 12px rgba(0, 123, 255, 0.3)',
                                    transition: 'all 0.3s ease',
                                    '&:hover': {
                                        backgroundColor: '#0069d9',
                                        boxShadow: '0 6px 16px rgba(0, 123, 255, 0.4)',
                                        transform: 'translateY(-2px)'
                                    },
                                    '&:active': {
                                        transform: 'translateY(0)'
                                    },
                                    '&.Mui-disabled': {
                                        backgroundColor: 'rgba(0, 123, 255, 0.6)',
                                        color: 'white'
                                    }
                                }}
                            >
                                {isLoading ? 'Creating Admin...' : 'Create Admin'}
                            </Button>
                        </Grid>
                    </Grid>
                </form>
            </Paper>
            <LoginDialog
                open={openLogin}
                onClose={handleLoginClose}
                onOtpLogin={handleOtpLogin}
                onLoginSuccess={handleLoginSuccess}
                onLoginError={handleLoginError}
            />
        </Box>
    );
};

export default AdminCreation;

